import React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { getMyCompanies, selectCompany } from "api/company.api";
import { QUERY_KEYS } from "config/queryKeys";
import { ROUTES } from "config/routes";
import { invalidateCompanyScopedQueries } from "lib/queryHelpers";

const getCompaniesArray = (data) => {
  const companies =
    data?.data?.companies || data?.companies || data?.data || [];
  return Array.isArray(companies) ? companies : [];
};

const getCompanyId = (company) => {
  return (
    company?.companyId ||
    company?._id ||
    company?.id ||
    company?.company?._id ||
    company?.company?.id ||
    null
  );
};

const getCompanyName = (company) => {
  return (
    company?.legalName ||
    company?.name ||
    company?.company?.legalName ||
    company?.company?.name ||
    null
  );
};

const getCompanyVatNumber = (company) => {
  return company?.vatNumber || company?.company?.vatNumber || "N/A";
};

const getCompanyRole = (company) => {
  return (
    company?.companyRole ||
    company?.role ||
    company?.membership?.companyRole ||
    null
  );
};

const CompanySelect = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation("pages");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: QUERY_KEYS.companies.mine(),
    queryFn: getMyCompanies,
    retry: false,
  });

  const companies = React.useMemo(() => getCompaniesArray(data), [data]);

  const { mutate: selectCompanyMutation, isPending } = useMutation({
    mutationFn: selectCompany,
    onSuccess: (response) => {
      const selectedCompany =
        response?.company || response?.data?.company || null;

      if (selectedCompany) {
        queryClient.setQueryData(QUERY_KEYS.companies.active(), {
          success: true,
          hasActiveCompany: true,
          company: selectedCompany,
          context: response?.context || response?.data?.context || null,
        });
      }

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.auth.me() });
      invalidateCompanyScopedQueries(queryClient);

      toast.success(t("companySelect.selected"));
      navigate(ROUTES.DASHBOARD, { replace: true });
    },
    onError: (err) => {
      toast.error(err?.message || t("companySelect.failedSelect"));
    },
  });

  React.useEffect(() => {
    if (!isLoading && !isError && companies.length === 0) {
      navigate(ROUTES.COMPANY_ONBOARDING, { replace: true });
    }
  }, [companies.length, isError, isLoading, navigate]);

  const handleSelect = (company) => {
    const companyId = getCompanyId(company);

    if (!companyId) {
      toast.error(t("companySelect.couldNotIdentify"));
      return;
    }

    selectCompanyMutation(companyId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4" />
          <p className="text-sm text-muted-foreground">
            {t("companySelect.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-6 text-center">
          <Icon
            name="AlertTriangle"
            size={40}
            className="mx-auto text-red-500 mb-4"
          />
          <h1 className="text-xl font-semibold text-foreground mb-2">
            {t("companySelect.failedLoad")}
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            {error?.message || t("companySelect.tryAgain")}
          </p>
          <Button onClick={() => window.location.reload()}>{t("companySelect.retry")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
      <div className="max-w-5xl mx-auto py-12">
        <div className="text-center mb-10">
          <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Icon name="Building2" size={28} className="text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {t("companySelect.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("companySelect.description")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {companies.map((company) => {
            const companyId = getCompanyId(company);
            const companyName =
              getCompanyName(company) || t("companySelect.unnamed");
            const vatNumber = getCompanyVatNumber(company);
            const companyRole = getCompanyRole(company);

            return (
              <button
                key={companyId || company?.membershipId || companyName}
                type="button"
                onClick={() => handleSelect(company)}
                disabled={isPending || !companyId}
                className="text-start bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-lg hover:border-primary/40 transition-all disabled:opacity-60"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      {companyName}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t("companySelect.vat")}: {vatNumber}
                    </p>
                    {companyRole && (
                      <p className="text-xs text-primary mt-3 font-medium">
                        {companyRole}
                      </p>
                    )}
                  </div>

                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <Icon
                      name="ChevronRight"
                      size={20}
                      className="text-primary rtl:scale-x-[-1]"
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="text-center mt-8">
          <Button
            variant="outline"
            onClick={() => navigate(ROUTES.COMPANY_ONBOARDING)}
            disabled={isPending}
          >
            {t("companySelect.createNew")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompanySelect;
